import { useState } from "react";
import { InfoIcon, SearchIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";

export function FilterSubMenuMultiselect<T extends string | number>({
  label,
  options,
  selected,
  setSelected,
  searchable = false,
}: {
  label: string;
  options: { value: T; label: string; tooltip?: string }[];
  selected: T[];
  setSelected: (selected: T[]) => void;
  searchable?: boolean;
}) {
  /* React Hooks */
  const [query, setQuery] = useState("");

  /* Handlers */
  const handleToggle = (value: T, checked: boolean) => {
    const next = checked
      ? [...selected, value]
      : selected.filter((v) => v !== value);
    mixpanelUtils.trackFilterAndSortOptionSelected(
      getScreenFromPathname(location.pathname),
      label,
      next,
    );
    setSelected(next);
  };

  const handleClearAll = () => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      getScreenFromPathname(location.pathname),
      label,
      [],
    );
    setSelected([]);
  };

  /* Render */
  const trimmedQuery = query.trim().toLowerCase();
  const visibleOptions =
    searchable && trimmedQuery
      ? options.filter((o) => o.label.toLowerCase().includes(trimmedQuery))
      : options;

  const renderOptions = () => {
    if (visibleOptions.length === 0) {
      return (
        <Text className="px-3 py-4 text-center text-sm text-slate-500">
          No {label.toLowerCase()} found
        </Text>
      );
    }
    return visibleOptions.map((option) => (
      <DropdownMenuCheckboxItem
        key={option.value}
        checked={selected.includes(option.value)}
        onCheckedChange={(checked) => {
          handleToggle(option.value, checked);
        }}
        onSelect={(e) => {
          e.preventDefault();
        }}
      >
        <Row className="items-center gap-1.5">
          {option.label}
          {option.tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  asChild
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <InfoIcon className="h-3 w-3 text-slate-400" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <Text className="max-w-[200px] text-xs">
                    {option.tooltip}
                  </Text>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Row>
      </DropdownMenuCheckboxItem>
    ));
  };

  const render = () => (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Row className="items-center gap-2">
          <span>{label}</span>
          {selected.length > 0 && (
            <Badge className="border-primary-700 bg-primary-300 px-1.5 py-0 text-[10px] leading-none text-slate-700">
              {selected.length}
            </Badge>
          )}
        </Row>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="p-0">
        {searchable && (
          <FilterSubMenuSearchHeader
            label={label}
            onChange={setQuery}
            value={query}
          />
        )}
        <Column className="max-h-[280px] overflow-y-auto">
          {renderOptions()}
        </Column>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            handleClearAll();
          }}
        >
          Clear all
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );

  return render();
}

/* -------------------------------------------------------------------------
 * Sub-components
 * ----------------------------------------------------------------------- */

const FilterSubMenuSearchHeader = ({
  label,
  onChange,
  value,
}: {
  label: string;
  onChange: (next: string) => void;
  value: string;
}) => {
  const render = () => (
    <Row className="sticky top-0 z-10 items-center gap-2 border-b border-slate-200 bg-white px-2 py-1.5">
      <SearchIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <input
        autoFocus
        className="h-7 flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
        onChange={(e) => {
          onChange(e.target.value);
        }}
        // Radix focuses the first item by default — let the input handle typing.
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        placeholder={`Search ${label.toLowerCase()}`}
        type="text"
        value={value}
      />
    </Row>
  );

  return render();
};
